import React, { useState, useEffect, useMemo, useRef, createElement } from 'react';
import budgetCategoriesData from '../data/budgetCategories.json';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Plus,
  Trash2,
  Users,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Info,
  BarChart3,
  PieChart as PieChartIcon,
  Download,
  Upload,
  Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import ToolLayout from '../components/ToolLayout';

const BudgetTool = () => {
  const [budget, setBudget] = useState({
    monthly_income: 0,
    family_members: [],
    emergency_fund_current: 0,
    expense_categories: [],
  });

  const [defaultCategories, setDefaultCategories] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', age: '', has_emergency_cash: false });
  const [newCategory, setNewCategory] = useState({ name: '', amount: '', description: '' });
  const [chartType, setChartType] = useState('pie');
  const [isLoading, setIsLoading] = useState(true);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [userClearedAll, setUserClearedAll] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [emergencyFundTargetYears, setEmergencyFundTargetYears] = useState(7);

  // Animation tracking
  const [totalsAnimating, setTotalsAnimating] = useState(false);
  const prevTotalExpenses = useRef(null);
  const prevFundingPct = useRef(null);
  const [fundAnimating, setFundAnimating] = useState(false);

  // Backend URL no longer needed - using embedded data

  useEffect(() => {
    if (!initialLoadDone) {
      loadDefaultCategories();
      setInitialLoadDone(true);
    }
  }, [initialLoadDone]);

  const loadDefaultCategories = () => {
    try {
      // Use embedded data directly - no API call needed
      const categories = budgetCategoriesData.map((cat) => ({
        ...cat,
        id: cat.id || `cat-${Date.now()}-${Math.random()}`,
      }));
      setDefaultCategories(categories);
      setBudget((prev) => ({
        ...prev,
        expense_categories: categories,
      }));
      setCategoriesLoaded(true);
      setUserClearedAll(false);
    } catch (error) {
      console.error('Failed to load default categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate emergency fund requirements
  const emergencyFundCalcs = useMemo(() => {
    const totalAge = budget.family_members.reduce((sum, member) => sum + member.age, 0);
    const requiredAmount = totalAge * 1000;
    const currentAmount = budget.emergency_fund_current || 0;
    const fundingPercentage = requiredAmount > 0 ? (currentAmount / requiredAmount) * 100 : 0;
    const remainingAmount = Math.max(0, requiredAmount - currentAmount);

    // Calculate monthly contribution based on user-selected timeline with aging family members
    let suggestedMonthlyContribution = 0;
    const targetYears = emergencyFundTargetYears;
    const familySize = budget.family_members.length;

    if (remainingAmount > 0 && familySize > 0) {
      // The target grows by $1,000 per family member per year
      const annualTargetIncrease = familySize * 1000;
      const totalTargetIncrease = annualTargetIncrease * targetYears;

      // Total amount needed = current shortfall + growth over time period
      const totalAmountNeeded = remainingAmount + totalTargetIncrease;

      // Monthly payment to reach the moving target - no caps, show true requirement
      suggestedMonthlyContribution = Math.max(totalAmountNeeded / (targetYears * 12), 25); // Minimum $25
    } else if (remainingAmount > 0 && familySize === 0) {
      // If no family members, use simple calculation
      suggestedMonthlyContribution = Math.max(remainingAmount / (targetYears * 12), 25); // Minimum $25
    }

    const monthsToFullFunding = targetYears * 12;
    const yearsToFullFunding = targetYears;

    return {
      totalAge,
      requiredAmount,
      currentAmount,
      fundingPercentage: Math.round(fundingPercentage * 10) / 10,
      remainingAmount,
      suggestedMonthlyContribution: Math.round(suggestedMonthlyContribution),
      monthsToFullFunding,
      yearsToFullFunding,
      targetYears,
    };
  }, [
    budget.family_members,
    budget.emergency_fund_current,
    budget.monthly_income,
    emergencyFundTargetYears,
  ]);

  // Calculate immediate emergency cash requirements
  const immediateCashCalcs = useMemo(() => {
    const totalFamilyMembers = budget.family_members.length;
    const membersWithCash = budget.family_members.filter(
      (member) => member.has_emergency_cash
    ).length;
    const membersNeedingCash = totalFamilyMembers - membersWithCash;
    const totalCashRequired = totalFamilyMembers * 500;
    const totalCashSecured = membersWithCash * 500;
    const cashShortfall = membersNeedingCash * 500;

    return {
      totalFamilyMembers,
      membersWithCash,
      membersNeedingCash,
      totalCashRequired,
      totalCashSecured,
      cashShortfall,
    };
  }, [budget.family_members]);

  // Calculate budget totals
  const budgetTotals = useMemo(() => {
    const totalExpenses = budget.expense_categories.reduce(
      (sum, cat) => sum + (parseFloat(cat.amount) || 0),
      0
    );
    const freeCashFlow = (budget.monthly_income || 0) - totalExpenses;
    const availableForDebt = Math.max(
      0,
      freeCashFlow - emergencyFundCalcs.suggestedMonthlyContribution
    );

    return {
      totalExpenses,
      freeCashFlow,
      availableForDebt,
    };
  }, [
    budget.monthly_income,
    budget.expense_categories,
    emergencyFundCalcs.suggestedMonthlyContribution,
  ]);

  // Animation triggers on budget total changes
  useEffect(() => {
    if (prevTotalExpenses.current !== null && prevTotalExpenses.current !== budgetTotals.totalExpenses) {
      setTotalsAnimating(true);
      setTimeout(() => setTotalsAnimating(false), 400);
    }
    prevTotalExpenses.current = budgetTotals.totalExpenses;
  }, [budgetTotals.totalExpenses]);

  // Animation trigger on emergency fund progress change
  useEffect(() => {
    if (prevFundingPct.current !== null && emergencyFundCalcs.fundingPercentage > prevFundingPct.current) {
      setFundAnimating(true);
      setTimeout(() => setFundAnimating(false), 400);
      if (emergencyFundCalcs.fundingPercentage >= 100 && prevFundingPct.current < 100) {
        toast.success('Emergency fund goal reached! Your family is financially prepared.');
      }
    }
    prevFundingPct.current = emergencyFundCalcs.fundingPercentage;
  }, [emergencyFundCalcs.fundingPercentage]);

  // Prepare chart data including free cash flow and debt categorization
  const chartData = useMemo(() => {
    const expenseData = budget.expense_categories
      .filter((cat) => {
        const amount = parseFloat(cat.amount) || 0;
        return amount > 0;
      })
      .map((cat) => ({
        name: cat.name,
        amount: parseFloat(cat.amount) || 0,
        type: cat.id?.startsWith('debt-') ? 'debt' : 'expense',
      }));

    if (budgetTotals.freeCashFlow > 0) {
      expenseData.push({
        name: 'Free Cash Flow',
        amount: budgetTotals.freeCashFlow,
        type: 'available',
      });
    }

    const totalForPercentage = expenseData.reduce((sum, item) => sum + item.amount, 0);

    return expenseData
      .map((item) => ({
        ...item,
        percentage:
          totalForPercentage > 0 ? ((item.amount / totalForPercentage) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [budget.expense_categories, budgetTotals.totalExpenses, budgetTotals.freeCashFlow]);

  // Chart colors - theme palette (dark + green accent): primary green, teals, warm accents
  const CHART_COLORS = [
    'hsl(158, 64%, 52%)',   // primary green
    'hsl(173, 58%, 45%)',   // teal
    'hsl(43, 74%, 66%)',    // gold
    'hsl(27, 87%, 67%)',    // orange
    'hsl(197, 50%, 50%)',   // blue
    'hsl(12, 76%, 61%)',    // coral
    'hsl(215, 25%, 55%)',   // slate (muted)
    'hsl(158, 40%, 45%)',   // green muted
    'hsl(43, 60%, 55%)',    // amber
    'hsl(197, 40%, 55%)',   // blue muted
  ];

  const addFamilyMember = () => {
    if (newMember.name && newMember.age) {
      setBudget((prev) => ({
        ...prev,
        family_members: [
          ...prev.family_members,
          {
            name: newMember.name,
            age: parseInt(newMember.age),
            has_emergency_cash: newMember.has_emergency_cash,
          },
        ],
      }));
      setNewMember({ name: '', age: '', has_emergency_cash: false });
      setShowAddMember(false);
    }
  };

  const removeFamilyMember = (index) => {
    setBudget((prev) => ({
      ...prev,
      family_members: prev.family_members.filter((_, i) => i !== index),
    }));
  };

  const toggleMemberEmergencyCash = (index) => {
    setBudget((prev) => ({
      ...prev,
      family_members: prev.family_members.map((member, i) =>
        i === index ? { ...member, has_emergency_cash: !member.has_emergency_cash } : member
      ),
    }));
  };

  const addCustomCategory = () => {
    if (newCategory.name && newCategory.amount) {
      const customCat = {
        id: Date.now().toString(),
        name: newCategory.name,
        amount: parseFloat(newCategory.amount),
        description: newCategory.description,
        is_custom: true,
      };
      setBudget((prev) => ({
        ...prev,
        expense_categories: [...prev.expense_categories, customCat],
      }));
      setNewCategory({ name: '', amount: '', description: '' });
      setShowAddCategory(false);
      setUserClearedAll(false);
    }
  };

  const removeCategory = (categoryId) => {
    setBudget((prev) => ({
      ...prev,
      expense_categories: prev.expense_categories.filter((cat) => cat.id !== categoryId),
    }));
  };

  const updateCategoryAmount = (categoryId, amount) => {
    setBudget((prev) => ({
      ...prev,
      expense_categories: prev.expense_categories.map((cat) =>
        cat.id === categoryId ? { ...cat, amount: parseFloat(amount) || 0 } : cat
      ),
    }));
  };

  const resetToDefaults = () => {
    try {
      // Use embedded data directly - no API call needed
      const categories = budgetCategoriesData.map((cat) => ({
        ...cat,
        id: cat.id || `cat-${Date.now()}-${Math.random()}`,
      }));
      setBudget((prev) => ({
        ...prev,
        expense_categories: categories,
      }));
      setCategoriesLoaded(true);
      setUserClearedAll(false);
      toast.success('Reset to default amounts!');
    } catch (error) {
      console.error('Failed to reset to defaults:', error);
      toast.error('Failed to reset to defaults');
    }
  };

  const clearAllAmounts = () => {
    setBudget((prev) => ({
      ...prev,
      expense_categories: [],
    }));
    setCategoriesLoaded(false);
    setUserClearedAll(true);
    toast.success('All expenses removed!');
  };

  const addEmergencyFundToBudget = () => {
    const emergencyFundCategory = {
      id: `emergency-fund-${Date.now()}`,
      name: 'Emergency Fund Contribution',
      amount: emergencyFundCalcs.suggestedMonthlyContribution,
      description: `Monthly contribution to reach $${emergencyFundCalcs.requiredAmount.toLocaleString()} emergency fund in ${emergencyFundCalcs.yearsToFullFunding} years`,
      is_custom: true,
    };

    // Check if emergency fund contribution already exists
    const existingIndex = budget.expense_categories.findIndex(
      (cat) => cat.name === 'Emergency Fund Contribution' || cat.name.includes('Emergency Fund')
    );

    if (existingIndex >= 0) {
      // Update existing emergency fund contribution
      setBudget((prev) => ({
        ...prev,
        expense_categories: prev.expense_categories.map((cat, index) =>
          index === existingIndex ? emergencyFundCategory : cat
        ),
      }));
      toast.success('Emergency fund contribution updated!');
    } else {
      // Add new emergency fund contribution
      setBudget((prev) => ({
        ...prev,
        expense_categories: [...prev.expense_categories, emergencyFundCategory],
      }));
      toast.success('Emergency fund contribution added to budget!');
    }

    setUserClearedAll(false);
  };

  const importDebtProfile = () => {
    // Create file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.style.display = 'none';

    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const fileContent = await file.text();
        const debtData = JSON.parse(fileContent);

        console.log('Imported debt data structure:', debtData);

        // Validate that this is a debt profile export
        if (!debtData.debts || !Array.isArray(debtData.debts)) {
          toast.error('Invalid debt profile file. Please export from the debt management tool.');
          return;
        }

        // Extract monthly income from debt data if available
        let monthlyIncome = budget.monthly_income; // Keep existing if no new income
        if (debtData.planning_data && debtData.planning_data.monthlyIncome) {
          monthlyIncome = debtData.planning_data.monthlyIncome;
          console.log('Found monthly income in planning_data:', monthlyIncome);
        } else if (debtData.monthlyIncome) {
          monthlyIncome = debtData.monthlyIncome;
          console.log('Found monthly income in root:', monthlyIncome);
        } else {
          console.log('No monthly income found in import data');
        }

        // Use the payment amounts from the debt_payoff_strategy
        const importedCategories = [];
        let totalMonthlyPayments = 0;

        // Get payment amounts from debt_payoff_strategy which has the calculated monthlyPayment
        const debtPayments = {};
        if (debtData.debt_payoff_strategy && Array.isArray(debtData.debt_payoff_strategy)) {
          console.log(
            'DEBUG: debt_payoff_strategy found with',
            debtData.debt_payoff_strategy.length,
            'items'
          );
          debtData.debt_payoff_strategy.forEach((strategyDebt, index) => {
            console.log(`DEBUG: Strategy debt ${index}:`, JSON.stringify(strategyDebt, null, 2));
            if (strategyDebt.monthlyPayment) {
              debtPayments[strategyDebt.id] = strategyDebt.monthlyPayment;
              console.log(
                `✅ Mapped strategy payment for ID ${strategyDebt.id}:`,
                strategyDebt.monthlyPayment
              );
            } else {
              console.warn(`❌ No monthlyPayment found for strategy debt ${strategyDebt.id}`);
            }
          });
          console.log('Final payments mapping:', debtPayments);
        } else {
          console.warn('❌ No debt_payoff_strategy found in import data');
        }

        for (const debt of debtData.debts) {
          console.log(`\n=== Processing debt: ${debt.name} (ID: ${debt.id}) ===`);
          console.log('DEBUG: Full debt object:', JSON.stringify(debt, null, 2));

          let monthlyPayment = 0;

          // Use the calculated monthlyPayment from debt_payoff_strategy
          if (debtPayments[debt.id]) {
            monthlyPayment = parseFloat(debtPayments[debt.id]);
            console.log(
              `✅ Using strategy payment for ${debt.name} (ID: ${debt.id}):`,
              monthlyPayment
            );
          }
          // Fallback to custom minimum payment if available
          else if (
            debt.data &&
            debt.data.custom_minimum_payment &&
            parseFloat(debt.data.custom_minimum_payment) > 0
          ) {
            monthlyPayment = parseFloat(debt.data.custom_minimum_payment);
            console.log(`✅ Using custom minimum payment for ${debt.name}:`, monthlyPayment);
          }
          // Try to calculate minimum payment for credit cards
          else if (debt.type === 'credit-card' && debt.data) {
            const balance = parseFloat(debt.data.current_balance) || 0;
            const rate = parseFloat(debt.data.annual_rate) || 0;
            const minPaymentPercent = parseFloat(debt.data.minimum_payment_percent) || 2.0;

            if (balance > 0) {
              // Use minimum payment percentage if available, otherwise 2% default
              monthlyPayment = Math.max(
                balance * (minPaymentPercent / 100),
                balance * (rate / 12) + 10
              );
              console.log(
                `✅ Calculated credit card minimum payment for ${debt.name} (balance: $${balance}):`,
                monthlyPayment
              );
            } else {
              monthlyPayment = 0; // Zero balance = zero payment
              console.log(`✅ Credit card ${debt.name} has zero balance, setting payment to $0`);
            }
          }
          // Try to calculate minimum payment for loans using data fields
          else if (
            (debt.type === 'auto-loan' ||
              debt.type === 'personal-loan' ||
              debt.type === 'student-loan' ||
              debt.type === 'mortgage') &&
            debt.data
          ) {
            const balance =
              parseFloat(debt.data.current_balance) || parseFloat(debt.data.remaining_balance) || 0;
            const rate = parseFloat(debt.data.annual_rate) || 0;
            const termMonths =
              parseInt(debt.data.term_months) || parseInt(debt.data.original_term_months) || 360;

            if (balance > 0 && rate > 0) {
              // Calculate loan payment using amortization formula
              const monthlyRate = rate / 12;
              const payment =
                (balance * (monthlyRate * Math.pow(1 + monthlyRate, termMonths))) /
                (Math.pow(1 + monthlyRate, termMonths) - 1);
              monthlyPayment = payment;
              console.log(
                `✅ Calculated loan payment for ${debt.name} (balance: $${balance}):`,
                monthlyPayment
              );
            } else if (balance === 0) {
              monthlyPayment = 0; // Zero balance = zero payment
              console.log(`✅ Loan ${debt.name} has zero balance, setting payment to $0`);
            }
          }

          // If still no payment found and debt has a balance, use placeholder
          if (monthlyPayment <= 0) {
            const balance =
              parseFloat(debt.data?.current_balance) ||
              parseFloat(debt.data?.remaining_balance) ||
              0;
            if (balance > 0) {
              console.warn(
                `❌ No payment amount found for ${debt.name} (ID: ${debt.id}) with balance $${balance} - available payment IDs:`,
                Object.keys(debtPayments)
              );
              monthlyPayment = 50; // Minimal placeholder only for debts with balances
            } else {
              console.log(`✅ ${debt.name} has zero balance, payment set to $0`);
              monthlyPayment = 0; // Zero payment for zero balance debts
            }
          }

          // Add recurring extra payments
          if (debt.additionalPayments && debt.additionalPayments.recurring_extra) {
            const extraPayment = parseFloat(debt.additionalPayments.recurring_extra) || 0;
            monthlyPayment += extraPayment;
            console.log(`Added extra payment for ${debt.name}:`, extraPayment);
          }

          totalMonthlyPayments += monthlyPayment;

          // Create budget category for this debt
          const category = {
            id: `debt-${debt.id}`,
            name: `${debt.name} ${debt.customEmoji || ''}`.trim(),
            amount: Math.round(monthlyPayment * 100) / 100,
            description: `${debt.type.replace('-', ' ')} payment - ${debt.data.interest_rate_display || (debt.data.annual_rate * 100).toFixed(2)}% APR`,
            is_custom: true,
          };

          importedCategories.push(category);
          console.log(`Created category for ${debt.name}:`, category);
        }

        // Remove existing debt categories and add imported ones, plus update income
        setBudget((prev) => ({
          ...prev,
          monthly_income: monthlyIncome,
          expense_categories: [
            ...prev.expense_categories.filter((cat) => !cat.id?.startsWith('debt-')), // Remove existing debt categories
            ...importedCategories,
          ],
        }));

        setUserClearedAll(false);
        const incomeUpdated = monthlyIncome !== budget.monthly_income;
        toast.success(
          `Debt data loaded! ${importedCategories.length} debt payments ($${Math.round(totalMonthlyPayments).toLocaleString()}/month)${incomeUpdated ? ` and monthly income ($${monthlyIncome.toLocaleString()})` : ''}.`
        );
      } catch (error) {
        console.error('Error parsing debt file:', error);
        toast.error(
          "Error reading debt file. Please make sure it's a valid JSON file saved from the debt management tool."
        );
      }

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const saveBudget = () => {
    // Budget data is automatically saved to localStorage - no backend needed
    // This function is kept for UI compatibility but does nothing
    toast.success('Budget data is automatically saved locally (no server storage)');
  };

  // Export budget to JSON file
  const exportBudget = () => {
    try {
      const exportData = {
        budget_info: {
          monthly_income: budget.monthly_income,
          family_members: budget.family_members,
          emergency_fund_current: budget.emergency_fund_current,
          emergency_fund_target_years: emergencyFundTargetYears,
          expense_categories: budget.expense_categories,
        },
        calculations: {
          emergency_fund: emergencyFundCalcs,
          immediate_cash: immediateCashCalcs,
          total_expenses: budgetTotals.totalExpenses,
          free_cash_flow: budgetTotals.freeCashFlow,
          available_for_debt: budgetTotals.availableForDebt,
        },
        export_date: new Date().toISOString(),
        version: '1.0',
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `budget-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Budget saved successfully!');
    } catch (error) {
      console.error('Error exporting budget:', error);
      toast.error('Failed to save budget');
    }
  };

  // Import budget from JSON file
  const importBudget = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.style.display = 'none';

    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const fileContent = await file.text();
        const importData = JSON.parse(fileContent);

        // Validate import data structure
        if (!importData.budget_info) {
          toast.error('Invalid budget file format. Please save from this tool first.');
          return;
        }

        // Import budget data
        setBudget({
          monthly_income: importData.budget_info.monthly_income || 0,
          family_members: importData.budget_info.family_members || [],
          emergency_fund_current: importData.budget_info.emergency_fund_current || 0,
          expense_categories: importData.budget_info.expense_categories || [],
        });

        // Import emergency fund target years if available
        if (importData.budget_info.emergency_fund_target_years) {
          setEmergencyFundTargetYears(importData.budget_info.emergency_fund_target_years);
        }

        setUserClearedAll(false);
        toast.success(
          `Budget loaded! ${importData.budget_info.family_members?.length || 0} family members and ${importData.budget_info.expense_categories?.length || 0} expense categories.`
        );
      } catch (error) {
        console.error('Error parsing budget file:', error);
        toast.error('Failed to load budget. Please check the file format.');
      }
    };

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  // Print budget
  const printBudget = () => {
    const printWindow = window.open('', '_blank');
    const familyNames = budget.family_members.map((member) => member.name).join(', ');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Budget Report - ${familyNames || 'Family'} Budget</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #4F46E5; padding-bottom: 15px; }
          .section { margin-bottom: 25px; }
          .section h3 { color: #4F46E5; border-bottom: 1px solid #E5E7EB; padding-bottom: 5px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .card { background: #F9FAFB; padding: 15px; border-radius: 8px; border: 1px solid #E5E7EB; }
          .expense-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #E5E7EB; }
          .expense-item:last-child { border-bottom: none; }
          .total { font-weight: bold; font-size: 1.1em; color: #059669; }
          .deficit { color: #DC2626; }
          .progress-bar { height: 10px; background: #E5E7EB; border-radius: 5px; overflow: hidden; margin: 5px 0; }
          .progress-fill { height: 100%; background: #10B981; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>💰 Budget Report</h1>
          <h2>${familyNames ? `${familyNames} Budget` : 'Family Budget'}</h2>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="grid">
          <div class="card">
            <h3>💵 Basic Information</h3>
            <p><strong>Monthly Income (After Taxes):</strong> $${budget.monthly_income?.toLocaleString() || '0'}</p>
            <p><strong>Family Members:</strong> ${budget.family_members?.length || 0}</p>
            ${
              budget.family_members
                ?.map(
                  (member) =>
                    `<p>• ${member.name} (Age ${member.age}) ${member.has_emergency_cash ? '✅ Has $500 emergency cash' : '❌ Needs $500 emergency cash'}</p>`
                )
                .join('') || ''
            }
          </div>
          
          <div class="card">
            <h3>🚨 Emergency Fund Status</h3>
            <p><strong>Target Amount:</strong> $${emergencyFundCalcs.requiredAmount?.toLocaleString() || '0'}</p>
            <p><strong>Current Balance:</strong> $${budget.emergency_fund_current?.toLocaleString() || '0'}</p>
            <p><strong>Funding Status:</strong> ${emergencyFundCalcs.fundingPercentage?.toFixed(1) || '0'}%</p>
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${Math.min(emergencyFundCalcs.fundingPercentage || 0, 100)}%"></div>
            </div>
            <p><strong>Remaining Needed:</strong> $${emergencyFundCalcs.remainingAmount?.toLocaleString() || '0'}</p>
            <p><strong>Suggested Monthly:</strong> $${Math.round(emergencyFundCalcs.suggestedMonthlyContribution || 0)} (${emergencyFundTargetYears}-year plan)</p>
          </div>
        </div>
        
        <div class="section">
          <h3>📊 Monthly Expenses</h3>
          <div class="card">
            ${
              budget.expense_categories
                ?.map(
                  (category) =>
                    `<div class="expense-item">
                <span>${category.name}</span>
                <span>$${category.amount?.toLocaleString() || '0'}</span>
              </div>`
                )
                .join('') || '<p>No expenses added</p>'
            }
            <div class="expense-item total">
              <span>Total Monthly Expenses</span>
              <span>$${budgetTotals.totalExpenses?.toLocaleString() || '0'}</span>
            </div>
          </div>
        </div>
        
        <div class="section">
          <h3>💰 Financial Summary</h3>
          <div class="card">
            <div class="expense-item">
              <span>Monthly Income</span>
              <span>$${budget.monthly_income?.toLocaleString() || '0'}</span>
            </div>
            <div class="expense-item">
              <span>Total Expenses</span>
              <span>$${budgetTotals.totalExpenses?.toLocaleString() || '0'}</span>
            </div>
            <div class="expense-item">
              <span>Emergency Fund Contribution</span>
              <span>$${Math.round(emergencyFundCalcs.suggestedMonthlyContribution || 0)}</span>
            </div>
            <div class="expense-item total ${budgetTotals.freeCashFlow < 0 ? 'deficit' : ''}">
              <span>Free Cash Flow</span>
              <span>$${budgetTotals.freeCashFlow?.toLocaleString() || '0'}</span>
            </div>
            <div class="expense-item total">
              <span>Available for Debt Payments</span>
              <span>$${budgetTotals.availableForDebt?.toLocaleString() || '0'}</span>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (isLoading) {
    return createElement(
      'div',
      { className: 'flex items-center justify-center min-h-screen bg-page' },
      createElement(
        'div',
        { className: 'text-center' },
        createElement('div', {
          className: 'animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4',
        }),
        createElement('p', { className: 'text-text-muted' }, 'Loading budget tool...')
      )
    );
  }

  return createElement(
    ToolLayout,
    {
      title: 'Budget Creation Tool',
      description:
        'Build a budget to see how much money you have left after bills. Make sure you have enough set aside for emergencies.',
      icon: DollarSign,
      guide: {
        intro: 'Build a household budget and plan your emergency fund.',
        steps: [
          'Enter your household size to figure out how much emergency savings you need.',
          'Add your income.',
          'Fill in your expenses.',
          'Check the charts to see where your money goes.',
          'Save for your binder.',
        ],
      },
    },
    createElement(
      'div',
      { className: 'space-y-8' },

      // Save/Load Buttons (top)
      createElement(
        'div',
        { className: 'flex items-center justify-end gap-2' },
        createElement(
          'button',
          {
            onClick: exportBudget,
            className:
              'px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 flex items-center gap-2 shadow-sm',
          },
          createElement(Download, { size: 18 }),
          'Save'
        ),
        createElement(
          'button',
          {
            onClick: importBudget,
            className:
              'px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 flex items-center gap-2 shadow-sm',
          },
          createElement(Upload, { size: 18 }),
          'Load'
        ),
        createElement(
          'button',
          {
            onClick: printBudget,
            className:
              'px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 flex items-center gap-2 shadow-sm',
          },
          createElement(Printer, { size: 18 }),
          'Print'
        )
      ),

      createElement(
        'div',
        { className: 'grid grid-cols-1 lg:grid-cols-2 gap-8 items-start' },

        // Left Column - Basic Info and Family
        createElement(
          'div',
          { className: 'space-y-6' },

          // Basic Information
          createElement(
            'div',
            { className: 'bg-card rounded-lg border border-border p-6' },
            createElement(
              'h2',
              { className: 'text-2xl font-semibold text-text-primary mb-4 flex items-center' },
              createElement(DollarSign, { className: 'mr-2 text-primary' }),
              'Basic Information'
            ),
            createElement(
              'div',
              { className: 'space-y-4' },
              createElement(
                'div',
                null,
                createElement(
                  'label',
                  { className: 'block text-sm font-medium text-text-primary mb-1' },
                  'Monthly Income (After Taxes)'
                ),
                createElement('input', {
                  type: 'number',
                  value: budget.monthly_income,
                  onChange: (e) =>
                    setBudget((prev) => ({
                      ...prev,
                      monthly_income: parseFloat(e.target.value) || 0,
                    })),
                  className:
                    'w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary',
                  placeholder: '5000',
                  min: '0',
                  step: '0.01',
                })
              )
            )
          ),

          // Family Members
          createElement(
            'div',
            { className: 'bg-card rounded-lg border border-border p-6' },
            createElement(
              'h2',
              { className: 'text-2xl font-semibold text-text-primary mb-4 flex items-center' },
              createElement(Users, { className: 'mr-2 text-primary' }),
              'Family Members'
            ),
            createElement(
              'div',
              { className: 'space-y-3' },

              // Family member list
              ...budget.family_members.map((member, index) =>
                createElement(
                  'div',
                  {
                    key: index,
                    className: 'flex items-center justify-between bg-surface-muted p-3 rounded-md',
                  },
                  createElement(
                    'div',
                    { className: 'flex items-center space-x-3 flex-1' },
                    createElement(
                      'div',
                      null,
                      createElement('span', { className: 'font-medium' }, member.name),
                      createElement(
                        'span',
                        { className: 'text-text-muted ml-2' },
                        `(${member.age} years old)`
                      )
                    )
                  ),
                  createElement(
                    'div',
                    { className: 'flex items-center space-x-3' },
                    createElement(
                      'label',
                      { className: 'flex items-center text-sm' },
                      createElement('input', {
                        type: 'checkbox',
                        checked: member.has_emergency_cash || false,
                        onChange: () => toggleMemberEmergencyCash(index),
                        className:
                          'mr-2 h-4 w-4 text-primary focus:ring-primary border-input rounded',
                      }),
                      createElement(
                        'span',
                        { className: 'text-primary font-medium' },
                        'Has $500 emergency cash'
                      )
                    ),
                    createElement(
                      'button',
                      {
                        onClick: () => removeFamilyMember(index),
                        className: 'text-destructive hover:text-text-primary',
                      },
                      createElement(Trash2, { size: 16 })
                    )
                  )
                )
              ),

              // Add member form or button
              showAddMember
                ? createElement(
                    'div',
                    { className: 'bg-primary/10 p-4 rounded-md' },
                    createElement(
                      'div',
                      { className: 'grid grid-cols-2 gap-3 mb-3' },
                      createElement('input', {
                        type: 'text',
                        placeholder: 'Name',
                        value: newMember.name,
                        onChange: (e) =>
                          setNewMember((prev) => ({ ...prev, name: e.target.value })),
                        className:
                          'px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary',
                      }),
                      createElement('input', {
                        type: 'number',
                        placeholder: 'Age',
                        value: newMember.age,
                        onChange: (e) => setNewMember((prev) => ({ ...prev, age: e.target.value })),
                        className:
                          'px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary',
                        min: '0',
                      })
                    ),
                    createElement(
                      'div',
                      { className: 'mb-3' },
                      createElement(
                        'label',
                        { className: 'flex items-center text-sm' },
                        createElement('input', {
                          type: 'checkbox',
                          checked: newMember.has_emergency_cash,
                          onChange: (e) =>
                            setNewMember((prev) => ({
                              ...prev,
                              has_emergency_cash: e.target.checked,
                            })),
                          className:
                            'mr-2 h-4 w-4 text-primary focus:ring-primary border-input rounded',
                        }),
                        createElement(
                          'span',
                          { className: 'text-primary font-medium' },
                          'Has $500 emergency cash available'
                        )
                      )
                    ),
                    createElement(
                      'div',
                      { className: 'flex space-x-2' },
                      createElement(
                        'button',
                        {
                          onClick: addFamilyMember,
                          className:
                            'px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90',
                        },
                        'Add Member'
                      ),
                      createElement(
                        'button',
                        {
                          onClick: () => setShowAddMember(false),
                          className:
                            'px-4 py-2 bg-surface-muted text-text-primary rounded-md hover:opacity-90',
                        },
                        'Cancel'
                      )
                    )
                  )
                : createElement(
                    'button',
                    {
                      onClick: () => setShowAddMember(true),
                      className:
                        'w-full py-2 border-2 border-dashed border-primary text-primary rounded-md hover:bg-primary/10 flex items-center justify-center',
                    },
                    createElement(Plus, { size: 16, className: 'mr-1' }),
                    'Add Family Member'
                  )
            )
          ),

          // Emergency Fund Status
          createElement(
            'div',
            { className: 'bg-card rounded-lg border border-border p-6' },
            createElement(
              'h2',
              { className: 'text-2xl font-semibold text-text-primary mb-4 flex items-center' },
              createElement(TrendingUp, { className: 'mr-2 text-primary' }),
              'Emergency Fund Status'
            ),
            createElement(
              'div',
              { className: 'space-y-4' },
              createElement(
                'div',
                null,
                createElement(
                  'label',
                  { className: 'block text-sm font-medium text-text-primary mb-1' },
                  'Current Emergency Fund Balance'
                ),
                createElement('input', {
                  type: 'number',
                  value: budget.emergency_fund_current,
                  onChange: (e) =>
                    setBudget((prev) => ({
                      ...prev,
                      emergency_fund_current: parseFloat(e.target.value) || 0,
                    })),
                  className:
                    'w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary',
                  placeholder: '0',
                  min: '0',
                  step: '0.01',
                })
              ),

              budget.family_members.length > 0
                ? [
                    // Immediate Emergency Cash Status
                    createElement(
                      'div',
                      {
                        key: 'immediate-cash',
                        className: `p-4 rounded-md mb-4 ${immediateCashCalcs.cashShortfall > 0 ? 'bg-destructive/10 border-l-4 border-destructive' : 'bg-primary/10 border-l-4 border-primary'}`,
                      },
                      createElement(
                        'div',
                        { className: 'flex items-start space-x-2 mb-3' },
                        immediateCashCalcs.cashShortfall > 0
                          ? createElement(AlertCircle, {
                              className: 'text-destructive mt-0.5 flex-shrink-0',
                              size: 16,
                            })
                          : createElement(CheckCircle, {
                              className: 'text-primary mt-0.5 flex-shrink-0',
                              size: 16,
                            }),
                        createElement(
                          'div',
                          { className: 'text-sm' },
                          createElement(
                            'p',
                            { className: 'font-medium mb-1' },
                            immediateCashCalcs.cashShortfall > 0
                              ? 'Immediate Emergency Cash Needed'
                              : 'Immediate Emergency Cash Secured'
                          ),
                          createElement(
                            'p',
                            { className: 'text-xs mb-2' },
                            'Each family member should have $500 on hand for emergencies — things like getting home, making calls, or buying what you need right away.'
                          ),
                          createElement(
                            'div',
                            { className: 'grid grid-cols-2 gap-4 text-xs' },
                            createElement(
                              'div',
                              null,
                              createElement(
                                'span',
                                { className: 'text-text-muted' },
                                'Family Members:'
                              ),
                              createElement(
                                'p',
                                { className: 'font-semibold' },
                                immediateCashCalcs.totalFamilyMembers.toString()
                              )
                            ),
                            createElement(
                              'div',
                              null,
                              createElement(
                                'span',
                                { className: 'text-text-muted' },
                                'Total Required:'
                              ),
                              createElement(
                                'p',
                                { className: 'font-semibold' },
                                `$${immediateCashCalcs.totalCashRequired.toLocaleString()}`
                              )
                            ),
                            createElement(
                              'div',
                              null,
                              createElement(
                                'span',
                                { className: 'text-text-muted' },
                                'Currently Secured:'
                              ),
                              createElement(
                                'p',
                                { className: 'font-semibold text-primary' },
                                `$${immediateCashCalcs.totalCashSecured.toLocaleString()}`
                              )
                            ),
                            createElement(
                              'div',
                              null,
                              createElement(
                                'span',
                                { className: 'text-text-muted' },
                                'Still Needed:'
                              ),
                              createElement(
                                'p',
                                {
                                  className: `font-semibold ${immediateCashCalcs.cashShortfall > 0 ? 'text-destructive' : 'text-primary'}`,
                                },
                                `$${immediateCashCalcs.cashShortfall.toLocaleString()}`
                              )
                            )
                          ),
                          // Immediate cash progress bar
                          createElement(
                            'div',
                            { className: 'mt-3 col-span-2' },
                            createElement(
                              'div',
                              { className: 'text-xs text-text-muted mb-1' },
                              'Immediate Emergency Cash Progress'
                            ),
                            createElement(
                              'div',
                              {
                                className:
                                  'w-full bg-muted rounded-full h-3 relative overflow-hidden',
                              },
                              createElement('div', {
                                className: `h-3 rounded-full transition-all duration-500 ${
                                  immediateCashCalcs.cashShortfall === 0
                                    ? 'bg-primary'
                                    : 'bg-destructive'
                                }`,
                                style: {
                                  width: `${immediateCashCalcs.totalCashRequired > 0 ? (immediateCashCalcs.totalCashSecured / immediateCashCalcs.totalCashRequired) * 100 : 0}%`,
                                },
                              }),
                              createElement(
                                'div',
                                { className: 'absolute inset-0 flex items-center justify-center' },
                                createElement(
                                  'span',
                                  { className: 'text-xs font-medium text-text-primary' },
                                  `${immediateCashCalcs.membersWithCash}/${immediateCashCalcs.totalFamilyMembers} members`
                                )
                              )
                            )
                          ),
                          immediateCashCalcs.membersNeedingCash > 0
                            ? createElement(
                                'p',
                                { className: 'text-xs text-destructive mt-2 font-medium' },
                                `👤 ${immediateCashCalcs.membersNeedingCash} family member(s) still need $500 emergency cash`
                              )
                            : null
                        )
                      )
                    ),

                    // Long-term Emergency Fund
                    createElement(
                      'div',
                      { key: 'long-term-fund', className: 'bg-primary/10 p-4 rounded-md' },
                      createElement(
                        'div',
                        { className: 'flex items-start space-x-2 mb-3' },
                        createElement(Info, {
                          className: 'text-primary mt-0.5 flex-shrink-0',
                          size: 16,
                        }),
                        createElement(
                          'div',
                          { className: 'text-sm text-text-primary' },
                          createElement(
                            'p',
                            { className: 'font-medium' },
                            'How your emergency fund target is calculated:'
                          ),
                          createElement(
                            'p',
                            null,
                            `$1,000 × total family age = $${emergencyFundCalcs.requiredAmount.toLocaleString()}`
                          ),
                          createElement(
                            'p',
                            null,
                            `Total family age: ${emergencyFundCalcs.totalAge} years`
                          ),
                          createElement(
                            'p',
                            { className: 'mt-2 text-xs' },
                            'As your family gets older, emergencies tend to cost more. Your target goes up $1,000 per person each year, so your monthly savings keep pace.'
                          ),
                          createElement(
                            'p',
                            { className: 'mt-1 text-xs font-medium text-text-primary' },
                            `💡 Monthly contribution calculated as ${((emergencyFundCalcs.suggestedMonthlyContribution / (budget.monthly_income || 1)) * 100).toFixed(1)}% of your income for a ${emergencyFundCalcs.yearsToFullFunding}-year timeline, accounting for ${budget.family_members.length} family members aging $${budget.family_members.length * 1000}/year.`
                          )
                        )
                      ),
                      createElement(
                        'div',
                        { className: 'grid grid-cols-2 gap-4 text-sm' },
                        createElement(
                          'div',
                          null,
                          createElement(
                            'span',
                            { className: 'text-text-muted' },
                            'Required Amount:'
                          ),
                          createElement(
                            'p',
                            { className: 'font-semibold text-lg' },
                            `$${emergencyFundCalcs.requiredAmount.toLocaleString()}`
                          )
                        ),
                        createElement(
                          'div',
                          null,
                          createElement(
                            'span',
                            { className: 'text-text-muted' },
                            'Funding Status:'
                          ),
                          createElement(
                            'div',
                            { className: 'mt-2 mb-3' },
                            // Progress bar container
                            createElement(
                              'div',
                              {
                                className:
                                  'w-full bg-muted rounded-full h-4 relative overflow-hidden',
                              },
                              // Progress bar fill
                              createElement('div', {
                                className: `h-4 rounded-full transition-all duration-500 ${fundAnimating ? 'animate-shimmer' : ''} ${
                                  emergencyFundCalcs.fundingPercentage >= 100
                                    ? 'bg-primary'
                                    : emergencyFundCalcs.fundingPercentage >= 75
                                      ? 'bg-primary'
                                      : emergencyFundCalcs.fundingPercentage >= 50
                                        ? 'bg-primary/80'
                                        : emergencyFundCalcs.fundingPercentage >= 25
                                          ? 'bg-destructive/80'
                                          : 'bg-destructive'
                                }`,
                                style: {
                                  width: `${Math.min(emergencyFundCalcs.fundingPercentage, 100)}%`,
                                },
                              }),
                              // Progress percentage text overlay
                              createElement(
                                'div',
                                { className: 'absolute inset-0 flex items-center justify-center' },
                                createElement(
                                  'span',
                                  {
                                    className: `text-xs font-bold text-text-primary drop-shadow-sm ${fundAnimating ? 'animate-bounce-scale' : ''}`,
                                  },
                                  `${emergencyFundCalcs.fundingPercentage.toFixed(1)}%`
                                )
                              )
                            )
                          ),
                          createElement(
                            'div',
                            {
                              className:
                                'flex items-center justify-between text-xs text-text-muted',
                            },
                            createElement(
                              'span',
                              null,
                              `$${emergencyFundCalcs.currentAmount.toLocaleString()}`
                            ),
                            createElement(
                              'span',
                              { className: 'flex items-center' },
                              `$${emergencyFundCalcs.requiredAmount.toLocaleString()}`,
                              emergencyFundCalcs.fundingPercentage >= 100
                                ? createElement(CheckCircle, {
                                    className: 'ml-1 text-primary',
                                    size: 14,
                                  })
                                : createElement(AlertCircle, {
                                    className: 'ml-1 text-destructive',
                                    size: 14,
                                  })
                            )
                          )
                        ),
                        createElement(
                          'div',
                          null,
                          createElement(
                            'span',
                            { className: 'text-text-muted' },
                            'Remaining Needed:'
                          ),
                          createElement(
                            'p',
                            { className: 'font-semibold text-lg' },
                            `$${emergencyFundCalcs.remainingAmount.toLocaleString()}`
                          )
                        ),
                        createElement(
                          'div',
                          null,
                          createElement(
                            'span',
                            { className: 'text-text-muted mb-2 block' },
                            'Target Timeline:'
                          ),
                          createElement(
                            'div',
                            { className: 'space-y-3' },
                            // Slider
                            createElement(
                              'div',
                              { className: 'flex items-center space-x-4' },
                              createElement('input', {
                                type: 'range',
                                min: 1,
                                max: 15,
                                value: emergencyFundTargetYears,
                                onChange: (e) =>
                                  setEmergencyFundTargetYears(parseInt(e.target.value)),
                                className:
                                  'flex-1 h-2 bg-muted rounded-lg cursor-pointer accent-primary',
                                style: {
                                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((emergencyFundTargetYears - 1) / 14) * 100}%, #e2e8f0 ${((emergencyFundTargetYears - 1) / 14) * 100}%, #e2e8f0 100%)`,
                                },
                              }),
                              createElement(
                                'div',
                                { className: 'text-right min-w-[60px]' },
                                createElement(
                                  'span',
                                  { className: 'font-semibold text-lg text-primary' },
                                  `${emergencyFundTargetYears} ${emergencyFundTargetYears === 1 ? 'year' : 'years'}`
                                )
                              )
                            ),
                            // Timeline labels
                            createElement(
                              'div',
                              { className: 'flex justify-between text-xs text-text-muted px-1' },
                              createElement('span', null, 'Aggressive\n(1 year)'),
                              createElement('span', null, 'Moderate\n(7 years)'),
                              createElement('span', null, 'Conservative\n(15 years)')
                            ),
                            // Payment details
                            createElement(
                              'div',
                              { className: 'mt-2 p-2 bg-surface-muted rounded text-sm' },
                              createElement(
                                'p',
                                { className: 'text-text-primary' },
                                `Monthly payment: `,
                                createElement(
                                  'span',
                                  { className: 'font-semibold text-primary' },
                                  `$${Math.round(emergencyFundCalcs.suggestedMonthlyContribution)}`
                                ),
                                ` (${((emergencyFundCalcs.suggestedMonthlyContribution / (budget.monthly_income || 1)) * 100).toFixed(1)}% of income)`
                              )
                            )
                          )
                        )
                      ),

                      // Add Emergency Fund to Budget Button
                      createElement(
                        'div',
                        { className: 'mt-6 p-4 bg-primary/10 border border-primary rounded-lg' },
                        createElement(
                          'div',
                          { className: 'flex items-center justify-between' },
                          createElement(
                            'div',
                            null,
                            createElement(
                              'h4',
                              { className: 'font-semibold text-text-primary' },
                              'Our Suggestion'
                            ),
                            createElement(
                              'p',
                              { className: 'text-sm text-primary mt-1' },
                              `Add $${emergencyFundCalcs.suggestedMonthlyContribution}/month to your budget (${((emergencyFundCalcs.suggestedMonthlyContribution / (budget.monthly_income || 1)) * 100).toFixed(1)}% of income)`
                            )
                          ),
                          createElement(
                            'button',
                            {
                              onClick: addEmergencyFundToBudget,
                              className:
                                'px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 text-sm font-medium flex items-center',
                            },
                            createElement(Plus, { size: 16, className: 'mr-1' }),
                            'Add to Budget'
                          )
                        )
                      )
                    ),
                  ]
                : null
            )
          )
        ),

        // Right Column - Expenses
        createElement(
          'div',
          { className: 'space-y-6 flex flex-col' },

          // Monthly Expenses
          createElement(
            'div',
            { className: 'bg-card rounded-lg border border-border p-6 flex-1 flex flex-col' },

            // Header Section
            createElement(
              'div',
              { className: 'mb-6' },
              createElement(
                'h2',
                { className: 'text-2xl font-semibold text-text-primary flex items-center mb-3' },
                createElement(BarChart3, { className: 'mr-2 text-primary' }),
                'Monthly Expenses'
              ),

              // Action Buttons - Main Actions
              createElement(
                'div',
                { className: 'flex flex-wrap gap-3 mb-4' },
                createElement(
                  'button',
                  {
                    onClick: () => setShowAddCategory(true),
                    className:
                      'px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center font-medium shadow-sm',
                  },
                  createElement(Plus, { size: 18, className: 'mr-2' }),
                  'Add Custom Expense'
                ),
                createElement(
                  'button',
                  {
                    onClick: importDebtProfile,
                    className:
                      'px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 flex items-center font-medium shadow-sm',
                    title: 'Load debt payments from a saved debt management file',
                  },
                  createElement(
                    'svg',
                    {
                      className: 'w-5 h-5 mr-2',
                      fill: 'none',
                      stroke: 'currentColor',
                      viewBox: '0 0 24 24',
                    },
                    createElement('path', {
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                      strokeWidth: 2,
                      d: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10',
                    })
                  ),
                  'Load Debt Data'
                )
              ),

              // Secondary Actions
              createElement(
                'div',
                { className: 'flex flex-wrap gap-2' },
                createElement(
                  'button',
                  {
                    onClick: resetToDefaults,
                    className:
                      'px-3 py-1.5 bg-surface-muted text-text-primary rounded-md hover:opacity-90 text-sm border border-border',
                  },
                  'Reset to Defaults'
                ),
                createElement(
                  'button',
                  {
                    onClick: clearAllAmounts,
                    className:
                      'px-3 py-1.5 bg-destructive/10 text-destructive rounded-md hover:opacity-90 text-sm border border-destructive',
                  },
                  'Clear All'
                )
              ),

              // Import Tip - Moved to bottom and made more concise
              createElement(
                'div',
                { className: 'mt-4 p-3 bg-primary/10 border-l-4 border-primary rounded-r-md' },
                createElement(
                  'div',
                  { className: 'flex items-start' },
                  createElement('div', { className: 'text-primary mr-2 mt-0.5' }, '💡'),
                  createElement(
                    'div',
                    { className: 'text-sm text-text-primary' },
                    createElement('span', { className: 'font-medium' }, 'Quick Load: '),
                    'Save your debt data from the Debt Management Tool, then load it here to automatically populate debt payments.'
                  )
                )
              )
            ),

            userClearedAll
              ? createElement(
                  'div',
                  { className: 'mb-4 p-4 bg-surface-muted border-l-4 border-border rounded-md' },
                  createElement(
                    'div',
                    { className: 'flex items-center' },
                    createElement(Info, {
                      className: 'text-text-muted mr-2 flex-shrink-0',
                      size: 18,
                    }),
                    createElement(
                      'span',
                      { className: 'text-sm text-text-primary' },
                      createElement('strong', null, '📝 All expenses cleared.'),
                      ' Add your own expenses, click "Reset Defaults" to reload suggested amounts, or "Load Debt Data" to load payments from a saved debt management file.'
                    )
                  )
                )
              : categoriesLoaded
                ? createElement(
                    'div',
                    { className: 'mb-4 p-4 bg-primary/10 border-l-4 border-primary rounded-md' },
                    createElement(
                      'div',
                      { className: 'flex items-center' },
                      createElement(CheckCircle, {
                        className: 'text-primary mr-2 flex-shrink-0',
                        size: 18,
                      }),
                      createElement(
                        'span',
                        { className: 'text-sm text-primary' },
                        createElement('strong', null, '✅ Realistic amounts preloaded!'),
                        ` Total: $${budgetTotals.totalExpenses.toLocaleString()}. These are typical household expenses to help you get started. Adjust any amount to match your actual expenses.`
                      )
                    )
                  )
                : createElement(
                    'div',
                    { className: 'mb-4 p-4 bg-primary/10 border-l-4 border-primary rounded-md' },
                    createElement(
                      'div',
                      { className: 'flex items-center' },
                      createElement('div', {
                        className:
                          'animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2',
                      }),
                      createElement(
                        'span',
                        { className: 'text-sm text-text-primary' },
                        'Loading suggested expense amounts...'
                      )
                    )
                  ),

            categoriesLoaded && !userClearedAll
              ? createElement(
                  'div',
                  { className: 'mb-3 p-2 bg-surface-muted rounded-md text-center' },
                  createElement(
                    'span',
                    { className: 'text-sm font-medium text-text-primary' },
                    `📊 Total Monthly Expenses: $${budgetTotals.totalExpenses.toLocaleString()}`
                  )
                )
              : null,

            // Separate debt payments from other expenses
            createElement(
              'div',
              { className: 'space-y-4 flex-1 min-h-[400px] max-h-[800px] overflow-y-auto' },

              // Debt Payments Section (if any exist)
              budget.expense_categories.filter((cat) => cat.id?.startsWith('debt-')).length > 0
                ? createElement(
                    'div',
                    { className: 'bg-primary/10 border border-primary rounded-lg p-4' },
                    createElement(
                      'h4',
                      { className: 'font-semibold text-text-primary mb-3 flex items-center' },
                      createElement(
                        'svg',
                        {
                          className: 'w-4 h-4 mr-2',
                          fill: 'none',
                          stroke: 'currentColor',
                          viewBox: '0 0 24 24',
                        },
                        createElement('path', {
                          strokeLinecap: 'round',
                          strokeLinejoin: 'round',
                          strokeWidth: 2,
                          d: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
                        })
                      ),
                      `Debt Payments (${budget.expense_categories.filter((cat) => cat.id?.startsWith('debt-')).length})`
                    ),
                    createElement(
                      'div',
                      { className: 'space-y-2 max-h-48 overflow-y-auto' },
                      ...budget.expense_categories
                        .filter((cat) => cat.id?.startsWith('debt-'))
                        .map((category) =>
                          createElement(
                            'div',
                            {
                              key: category.id,
                              className:
                                'flex items-center justify-between p-2 bg-white rounded border',
                            },
                            createElement(
                              'div',
                              { className: 'flex-1' },
                              createElement(
                                'div',
                                { className: 'flex items-center' },
                                createElement(
                                  'span',
                                  { className: 'font-medium text-sm' },
                                  category.name
                                ),
                                createElement(
                                  'button',
                                  {
                                    onClick: () => removeCategory(category.id),
                                    className: 'ml-2 text-destructive hover:text-text-primary',
                                    title: 'Remove debt payment',
                                  },
                                  createElement(Trash2, { size: 12 })
                                )
                              ),
                              category.description
                                ? createElement(
                                    'p',
                                    { className: 'text-xs text-text-muted' },
                                    category.description
                                  )
                                : null
                            ),
                            createElement(
                              'div',
                              { className: 'w-28' },
                              createElement('input', {
                                type: 'number',
                                value: category.amount || '',
                                onChange: (e) => updateCategoryAmount(category.id, e.target.value),
                                className:
                                  'w-full px-2 py-1 text-sm font-medium border border-input rounded focus:outline-none focus:ring-1 focus:ring-primary text-right bg-background text-foreground',
                                placeholder: '0',
                                min: '0',
                                step: '0.01',
                              })
                            )
                          )
                        )
                    )
                  )
                : null,

              // Regular Expenses Section
              createElement(
                'div',
                { className: 'space-y-3' },
                createElement(
                  'h4',
                  { className: 'font-semibold text-text-primary flex items-center' },
                  createElement(
                    'svg',
                    {
                      className: 'w-4 h-4 mr-2',
                      fill: 'none',
                      stroke: 'currentColor',
                      viewBox: '0 0 24 24',
                    },
                    createElement('path', {
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                      strokeWidth: 2,
                      d: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
                    })
                  ),
                  `Regular Expenses (${budget.expense_categories.filter((cat) => !cat.id?.startsWith('debt-')).length})`
                ),
                ...budget.expense_categories
                  .filter((cat) => !cat.id?.startsWith('debt-'))
                  .map((category) =>
                    createElement(
                      'div',
                      {
                        key: category.id,
                        className:
                          'flex items-center justify-between p-3 bg-surface-muted rounded-md',
                      },
                      createElement(
                        'div',
                        { className: 'flex-1 min-w-0' },
                        createElement(
                          'div',
                          { className: 'flex items-center' },
                          createElement(
                            'span',
                            { className: 'font-medium text-sm text-text-primary' },
                            category.name
                          ),
                          createElement(
                            'button',
                            {
                              onClick: () => removeCategory(category.id),
                              className: 'ml-2 text-destructive hover:text-text-primary',
                              title: 'Remove expense',
                            },
                            createElement(Trash2, { size: 14 })
                          )
                        ),
                        category.description
                          ? createElement(
                              'p',
                              { className: 'text-xs text-foreground/90 mt-0.5' },
                              category.description
                            )
                          : null
                      ),
                      createElement(
                        'div',
                        { className: 'w-32' },
                        createElement('input', {
                          type: 'number',
                          value: category.amount || '',
                          onChange: (e) => updateCategoryAmount(category.id, e.target.value),
                          className:
                            'w-full px-3 py-2 text-sm font-medium border border-input rounded focus:outline-none focus:ring-1 focus:ring-primary text-right bg-background text-foreground',
                          placeholder: '0',
                          min: '0',
                          step: '0.01',
                        })
                      )
                    )
                  )
              ),

              // Add category form
              showAddCategory
                ? createElement(
                    'div',
                    { className: 'mt-4 bg-primary/10 p-4 rounded-md' },
                    createElement(
                      'div',
                      { className: 'space-y-3' },
                      createElement('input', {
                        type: 'text',
                        placeholder: 'Expense Name',
                        value: newCategory.name,
                        onChange: (e) =>
                          setNewCategory((prev) => ({ ...prev, name: e.target.value })),
                        className:
                          'w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary',
                      }),
                      createElement('input', {
                        type: 'number',
                        placeholder: 'Monthly Amount',
                        value: newCategory.amount,
                        onChange: (e) =>
                          setNewCategory((prev) => ({ ...prev, amount: e.target.value })),
                        className:
                          'w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary',
                        min: '0',
                        step: '0.01',
                      }),
                      createElement('input', {
                        type: 'text',
                        placeholder: 'Description (Optional)',
                        value: newCategory.description,
                        onChange: (e) =>
                          setNewCategory((prev) => ({ ...prev, description: e.target.value })),
                        className:
                          'w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary',
                      }),
                      createElement(
                        'div',
                        { className: 'flex space-x-2' },
                        createElement(
                          'button',
                          {
                            onClick: addCustomCategory,
                            className:
                              'px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90',
                          },
                          'Add Expense'
                        ),
                        createElement(
                          'button',
                          {
                            onClick: () => setShowAddCategory(false),
                            className:
                              'px-4 py-2 bg-surface-muted text-text-primary rounded-md hover:opacity-90',
                          },
                          'Cancel'
                        )
                      )
                    )
                  )
                : null
            )
          )
        )
      ),

      // Full-width sections outside the grid
      createElement(
        'div',
        { className: 'space-y-8' },

        // Budget Summary
        createElement(
          'div',
          { className: 'bg-card rounded-lg border border-border p-6' },
          createElement(
            'h2',
            { className: 'text-2xl font-semibold text-text-primary mb-4' },
            'Budget Summary'
          ),
          createElement(
            'div',
            { className: 'grid grid-cols-2 gap-4 text-sm' },
            createElement(
              'div',
              { className: 'p-3 bg-primary/10 rounded-md' },
              createElement('span', { className: 'text-text-muted' }, 'Monthly Income:'),
              createElement(
                'p',
                { className: 'font-semibold text-xl text-primary' },
                `$${budget.monthly_income.toLocaleString()}`
              )
            ),
            createElement(
              'div',
              { className: 'p-3 bg-destructive/10 rounded-md' },
              createElement('span', { className: 'text-text-muted' }, 'Total Expenses:'),
              createElement(
                'p',
                { className: `font-semibold text-xl text-destructive ${totalsAnimating ? 'animate-bounce-scale' : ''}` },
                `$${budgetTotals.totalExpenses.toLocaleString()}`
              )
            ),
            createElement(
              'div',
              { className: 'p-3 bg-card rounded-md border border-border' },
              createElement('span', { className: 'text-text-muted' }, 'Free Cash Flow:'),
              createElement(
                'p',
                {
                  className: `font-semibold text-xl ${budgetTotals.freeCashFlow >= 0 ? 'text-primary' : 'text-destructive'} ${totalsAnimating ? 'animate-bounce-scale' : ''}`,
                },
                `$${budgetTotals.freeCashFlow.toLocaleString()}`
              )
            ),
            createElement(
              'div',
              { className: 'p-3 bg-card rounded-md border border-border' },
              createElement('span', { className: 'text-text-muted' }, 'Available for Debt:'),
              createElement(
                'p',
                {
                  className: `font-semibold text-xl ${budgetTotals.availableForDebt >= 0 ? 'text-primary' : 'text-destructive'}`,
                },
                `$${budgetTotals.availableForDebt.toLocaleString()}`
              )
            )
          ),

          emergencyFundCalcs.remainingAmount > 0
            ? createElement(
                'div',
                { className: 'mt-2 p-3 bg-primary/10 border-l-4 border-primary' },
                createElement(
                  'div',
                  { className: 'flex items-center' },
                  createElement(Info, { className: 'text-primary mr-2', size: 16 }),
                  createElement(
                    'span',
                    { className: 'text-sm text-text-primary' },
                    `Suggested $${emergencyFundCalcs.suggestedMonthlyContribution} monthly for emergency fund`
                  )
                )
              )
            : null
        ),

        // Charts - Full Width
        chartData.length > 0
          ? createElement(
              'div',
              { className: 'bg-card rounded-lg border border-border p-8' },
              createElement(
                'div',
                { className: 'flex items-center justify-between mb-4' },
                createElement(
                  'h2',
                  { className: 'text-2xl font-semibold text-text-primary' },
                  'Budget Breakdown'
                ),
                createElement(
                  'div',
                  { className: 'flex space-x-2' },
                  createElement(
                    'button',
                    {
                      onClick: () => setChartType('pie'),
                      className: `p-2 rounded-md ${chartType === 'pie' ? 'bg-primary text-primary-foreground' : 'bg-surface-muted text-text-primary'}`,
                    },
                    createElement(PieChartIcon, { size: 20 })
                  ),
                  createElement(
                    'button',
                    {
                      onClick: () => setChartType('bar'),
                      className: `p-2 rounded-md ${chartType === 'bar' ? 'bg-primary text-primary-foreground' : 'bg-surface-muted text-text-primary'}`,
                    },
                    createElement(BarChart3, { size: 20 })
                  )
                )
              ),

              createElement(
                'div',
                { className: 'grid lg:grid-cols-2 gap-8' },

                // Chart Section
                createElement(
                  'div',
                  { className: 'h-[600px]' },
                  createElement(
                    ResponsiveContainer,
                    { width: '100%', height: '100%' },
                    chartType === 'pie'
                      ? createElement(
                          PieChart,
                          null,
                          createElement(
                            Pie,
                            {
                              data: chartData,
                              cx: '50%',
                              cy: '50%',
                              outerRadius: 180,
                              fill: 'hsl(var(--primary))',
                              dataKey: 'amount',
                              label: false,
                            },
                            ...chartData.map((entry, index) =>
                              createElement(Cell, {
                                key: `cell-${index}`,
                                fill:
                                  entry.type === 'available'
                                    ? 'hsl(var(--primary))'
                                    : entry.type === 'debt'
                                      ? 'hsl(var(--destructive))'
                                      : CHART_COLORS[index % CHART_COLORS.length],
                              })
                            )
                          ),
                          createElement(Tooltip, {
                            contentStyle: {
                              backgroundColor: 'var(--surface)',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: 'var(--radius)',
                              color: 'var(--text-primary)',
                              padding: '10px 14px',
                            },
                            labelStyle: { color: 'var(--text-primary)' },
                            itemStyle: { color: 'var(--text-primary)' },
                            formatter: (value, name, props) => [
                              `$${value.toLocaleString()}`,
                              `${props.payload.name} (${props.payload.percentage}%)`,
                            ],
                          })
                        )
                      : createElement(
                          BarChart,
                          { data: chartData, margin: { bottom: 150, left: 20, right: 20 } },
                          createElement(CartesianGrid, { strokeDasharray: '3 3' }),
                          createElement(XAxis, {
                            dataKey: 'name',
                            angle: -45,
                            textAnchor: 'end',
                            height: 150,
                            interval: 0,
                            fontSize: 11,
                          }),
                          createElement(YAxis, null),
                          createElement(Tooltip, {
                            contentStyle: {
                              backgroundColor: 'var(--surface)',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: 'var(--radius)',
                              color: 'var(--text-primary)',
                              padding: '10px 14px',
                            },
                            labelStyle: { color: 'var(--text-primary)' },
                            itemStyle: { color: 'var(--text-primary)' },
                            formatter: (value, name, props) => [
                              `$${value.toLocaleString()}`,
                              `${props.payload.name} (${props.payload.percentage}%)`,
                            ],
                          }),
                          createElement(
                            Bar,
                            { dataKey: 'amount' },
                            ...chartData.map((entry, index) =>
                              createElement(Cell, {
                                key: `cell-${index}`,
                                fill:
                                  entry.type === 'available'
                                    ? 'hsl(var(--primary))'
                                    : entry.type === 'debt'
                                      ? 'hsl(var(--destructive))'
                                      : CHART_COLORS[index % CHART_COLORS.length],
                              })
                            )
                          )
                        )
                  )
                ),

                // Legend/Breakdown Section
                createElement(
                  'div',
                  { className: 'space-y-4' },
                  createElement(
                    'h3',
                    { className: 'text-xl font-semibold text-text-primary' },
                    'Breakdown by Category'
                  ),
                  createElement(
                    'div',
                    { className: 'space-y-3 max-h-[550px] overflow-y-auto' },
                    ...chartData.map((item, index) =>
                      createElement(
                        'div',
                        {
                          key: item.name,
                          className:
                            'flex items-center justify-between p-4 bg-surface-muted rounded-lg border border-border hover:bg-surface transition-colors',
                        },
                        createElement(
                          'div',
                          { className: 'flex items-center flex-1' },
                          createElement('div', {
                            className: 'w-6 h-6 rounded mr-4 flex-shrink-0',
                            style: {
                              backgroundColor:
                                item.type === 'available'
                                  ? '#10B981'
                                  : item.type === 'debt'
                                    ? '#F97316'
                                    : CHART_COLORS[index % CHART_COLORS.length],
                            },
                          }),
                          createElement(
                            'div',
                            { className: 'flex-1' },
                            createElement(
                              'span',
                              { className: 'text-base font-medium text-text-primary block' },
                              item.name
                            ),
                            item.type === 'available'
                              ? createElement(
                                  'span',
                                  { className: 'text-sm text-primary font-medium' },
                                  'Available for debt payments'
                                )
                              : null
                          )
                        ),
                        createElement(
                          'div',
                          { className: 'text-right ml-4 flex-shrink-0' },
                          createElement(
                            'div',
                            { className: 'text-lg font-bold text-text-primary' },
                            `$${item.amount.toLocaleString()}`
                          ),
                          createElement(
                            'div',
                            { className: 'text-base font-medium text-foreground/90 mt-0.5' },
                            `${item.percentage}% of total`
                          )
                        )
                      )
                    )
                  )
                )
              ),

              budgetTotals.freeCashFlow < 0
                ? createElement(
                    'div',
                    {
                      className:
                        'mt-6 p-4 bg-destructive/10 border-l-4 border-destructive rounded-lg',
                    },
                    createElement(
                      'div',
                      { className: 'flex items-center' },
                      createElement(AlertCircle, { className: 'text-destructive mr-3', size: 20 }),
                      createElement(
                        'span',
                        { className: 'text-base text-text-primary' },
                        createElement('strong', null, 'Budget Deficit:'),
                        ` You're spending $${Math.abs(budgetTotals.freeCashFlow).toLocaleString()} more than your income. Consider reducing expenses or increasing income.`
                      )
                    )
                  )
                : null
            )
          : null,

      )
    )
  );
};

export default BudgetTool;
